import type { UseQueryResult } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { LoginCard, LoginCardSubtitle, LoginCardTitle } from "./ui/login-card";
import { Button } from "./ui/button";

/**
 * The three states of a query, in one place.
 *
 * Written once because the alternative is every screen inventing its own — and
 * the state that gets forgotten is always the same one. A failed load that
 * renders as an empty list is indistinguishable from an empty workspace, which
 * is the same shape of lie as `listSites` answering `[]` to a caller who is not
 * allowed to read (`sitebrew-api#8`).
 */
export function QueryBoundary<T>({
  query,
  children,
}: {
  query: UseQueryResult<T>;
  children: (data: T) => ReactNode;
}) {
  if (query.isPending) {
    return (
      <LoginCard>
        <LoginCardTitle>Loading…</LoginCardTitle>
        <LoginCardSubtitle>One moment.</LoginCardSubtitle>
      </LoginCard>
    );
  }

  if (query.isError) {
    return (
      <LoginCard>
        <LoginCardTitle>That didn't load</LoginCardTitle>
        <LoginCardSubtitle>
          The console could not reach the API. Nothing is lost — try again.
        </LoginCardSubtitle>
        <Button className="mt-[.9rem] w-full" variant="outline" onClick={() => query.refetch()}>
          Try again
        </Button>
      </LoginCard>
    );
  }

  return <>{children(query.data as T)}</>;
}
